import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import ServiceForm from "../../components/forms/ServiceForm";
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  UserGroupIcon,
  PhotoIcon, // Добавил для заглушки
} from "@heroicons/react/24/outline";
import { showError, showSuccess } from "../../utils/toast";

const ServicesManagementPage = () => {
  const { t } = useTranslation();
  const { apiCall } = useAuth();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadServices();
    loadCategories();
    loadEmployees();
  }, []);

  useEffect(() => {
    filterServices();
  }, [services, searchTerm, selectedCategory, statusFilter]);

  const loadServices = async () => {
    try {
      setIsLoading(true);
      const response = await apiCall("/companies/services");
      ("🔍 LoadServices response:", response);
      if (response && Array.isArray(response.services)) {
        setServices(response.services);
      } else {
        console.error("❌ Invalid services response:", response);
        setServices([]);
      }
    } catch (error) {
      console.error("Failed to load services:", error);
      showError(t("company.services.messages.load_error"));
    } finally {
      setIsLoading(false);
      if (response && response.services) {
        ("🔥 SERVICE:", response.services[0]);
      }
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiCall("/marketplace/categories");
      if (response.success) setCategories(response.categories || []);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await apiCall("/companies/employees");
      if (response && Array.isArray(response.employees))
        setEmployees(response.employees);
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  const filterServices = () => {
    let filtered = [...services];
    if (searchTerm) {
      filtered = filtered.filter(
        (service) =>
          service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          service.description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (selectedCategory)
      filtered = filtered.filter(
        (service) => service.category_id === selectedCategory,
      );
    if (statusFilter !== "all") {
      const isActive =
        statusFilter === t("company.services.card.status_active");
      filtered = filtered.filter((service) => service.is_active === isActive);
    }
    setFilteredServices(filtered);
  };

  const handleCreateService = () => {
    setEditingService(null);
    setShowForm(true);
  };
  const handleEditService = (service) => {
    setEditingService(service);
    setShowForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    try {
      ("🔍 Checking booking count for service:", serviceId);

      const bookingCountResponse = await apiCall(
        `/companies/services/${serviceId}/booking-count`,
      );

      if (!bookingCountResponse) {
        throw new Error(t("company.services.messages.check_history_error"));
      }

      const { booking_count, can_delete } = bookingCountResponse;

      let confirmMessage;

      if (can_delete) {
        confirmMessage = t(
          "company.services.messages.delete_confirm_permanent",
        );
      } else {
        confirmMessage = t(
          "company.services.messages.delete_confirm_deactivate",
        );
      }

      if (!window.confirm(confirmMessage)) return;

      // 🔥 ВАЖНО: просто вызываем без response
      await apiCall(`/companies/services/${serviceId}`, {
        method: "DELETE",
      });

      showSuccess(t("company.services.messages.delete_success"));
      await loadServices();
    } catch (error) {
      console.error("❌ DELETE ERROR:", error);
      showError(
        t("company.services.messages.delete_error") +
          (error.message || "Unknown error"),
      );
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      const url = editingService
        ? `/companies/services/${editingService.id}`
        : "/companies/services";

      const method = editingService ? "PUT" : "POST";

      let body;

      // 🔥 ЕСЛИ ЕСТЬ ФАЙЛ → FormData
      if (formData.image instanceof File) {
        body = new FormData();

        Object.keys(formData).forEach((key) => {
          if (formData[key] !== null && formData[key] !== undefined) {
            body.append(key, formData[key]);
          }
        });
      } else {
        // обычный JSON если без файла
        body = JSON.stringify(formData);
      }

      const response = await apiCall(url, {
        method,
        body,
        // ❗ НЕ СТАВЬ Content-Type если FormData
      });

      if (response && (response.service || response.success)) {
        setShowForm(false);
        setEditingService(null);
        await loadServices();

        showSuccess(
          t(
            "company.services.messages.service",
          )` ${editingService ? t("company.services.messages.save_success_updated") : t("company.services.messages.save_success_created")} successfully!`,
        );
      }
    } catch (error) {
      console.error("❌ UPDATE ERROR:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImageUrl = (service) => {
    if (!service.image_url && !service.thumbnail_path) return null;

    const getBackendBaseUrl = () => {
      if (import.meta.env.PROD) {
        return "https://zootel.shop";
      }
      return `${window.location.protocol}//${window.location.hostname}:4000`;
    };

    const baseUrl = getBackendBaseUrl();

    if (service.image_url) {
      return service.image_url.replace(/https?:\/\/[^/]+/, baseUrl);
    }

    return `${baseUrl}/${service.thumbnail_path}`;
  };

  const getCategoryName = (categoryId) =>
    categories.find((cat) => cat.id === categoryId)?.name || "General";

  const getEmployeeNames = (employeeIds) => {
    if (!employeeIds || employeeIds.length === 0)
      return t("company.services.card.no_employees");
    const names = employeeIds.map(
      (id) =>
        employees.find((emp) => emp.id === id)?.name ||
        t("company.services.card.staff_prefix") + `${id.substring(0, 4)}`,
    );
    return names.length > 2
      ? `${names.slice(0, 2).join(", ")} +${names.length - 2}`
      : names.join(", ");
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    if (timeString.includes("T"))
      return timeString.split("T")[1].substring(0, 5);
    return timeString.substring(0, 5);
  };

  const formatDuration = (minutes) =>
    minutes >= 60
      ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
      : `${minutes}m`;

  const getStatusBadge = (isActive) => (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm ${
        isActive ? "bg-green-500 text-white" : "bg-slate-800 text-white"
      }`}
    >
      {isActive
        ? t("company.services.card.status_active")
        : t("company.services.card.status_inactive")}
    </span>
  );

  return (
    <div className="min-h-screen pb-20">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-end mb-10">
          <div>
            <h1 className="text-3xl font-black text-slate-900 uppercase italic tracking-tighter">
              {t("company.services.title_label")}{" "}
              <span className="text-slate-300">
                {t("company.services.title_span")}
              </span>
            </h1>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">
              {t("company.services.subtitle")}
            </p>
          </div>
          <button
            onClick={handleCreateService}
            className="bg-slate-900 text-white px-6 py-3 rounded-2xl hover:bg-orange-600 transition-all duration-300 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-lg active:scale-95"
          >
            <PlusIcon className="w-5 h-5" /> {t("company.services.btn_add")}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white/70 backdrop-blur-md p-4 rounded-[28px] border border-slate-200 shadow-sm mb-10 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[280px] relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t("company.services.filters.search_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-100 rounded-xl text-xs font-bold focus:ring-2 focus:ring-orange-500/20 transition-all"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 bg-white border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 focus:outline-none"
          >
            <option value="">
              {t("company.services.filters.all_categories")}
            </option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-slate-200 rounded-[32px]"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="group bg-white rounded-[32px] border border-slate-200 overflow-hidden flex flex-col transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-2 hover:border-orange-200"
              >
                {/* Photo Section with Placeholder */}
                <div className="h-52 relative overflow-hidden bg-slate-50">
                  {(() => {
                    const imageUrl = getImageUrl(service);

                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      /* Стилизованная затычка */
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 relative">
                        <div
                          className="absolute inset-0 opacity-[0.03] pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h20v20H0V0zm20 20h20v20H20V20z' fill='%23000' fill-opacity='.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                          }}
                        />
                        <PhotoIcon className="w-12 h-12 text-slate-200 mb-2" />
                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-300">
                          {t("company.services.card.no_employees")}
                        </span>
                      </div>
                    );
                  })()}

                  <div className="absolute top-4 right-4 z-10">
                    {getStatusBadge(service.is_active)}
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-black text-slate-900 uppercase italic leading-none tracking-tighter">
                      {service.name}
                    </h3>
                    <span className="text-xl font-black text-orange-600 italic tracking-tighter">
                      ${service.price}
                    </span>
                  </div>

                  <p className="text-xs text-slate-400 mb-4 line-clamp-2 font-medium leading-relaxed uppercase">
                    {service.description ||
                      t("company.services.card.default_desc")}
                  </p>

                  {/* 🔥 ОСНОВНАЯ ИНФА */}
                  <div className="space-y-3 mb-6">
                    {/* Длительность */}
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <ClockIcon className="w-4 h-4 text-orange-500" />
                      <span>{formatDuration(service.duration)}</span>
                    </div>

                    {/* ВРЕМЯ РАБОТЫ */}
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <ClockIcon className="w-4 h-4 text-blue-500" />
                      <span>
                        {service.start_time && service.end_time
                          ? `${formatTime(service.start_time)} - ${formatTime(service.end_time)}`
                          : t("company.services.card.no_time_limit")}
                      </span>
                    </div>

                    {/* Категория */}
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <FunnelIcon className="w-4 h-4 text-orange-500" />
                      <span>{getCategoryName(service.category_id)}</span>
                    </div>

                    {/* Сотрудники */}
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <UserGroupIcon className="w-4 h-4 text-orange-500" />
                      <span className="truncate">
                        {getEmployeeNames(service.assigned_employees)}
                      </span>
                    </div>

                    {/* 🔥 ДОП (если есть лимиты) */}
                    {(service.min_price || service.max_price) && (
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <span>
                          {t("company.services.card.price_range")}: $
                          {service.min_price || 0} - ${service.max_price || "∞"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-auto flex gap-3 pt-4 border-t border-slate-50">
                    <button
                      onClick={() => handleEditService(service)}
                      className="flex-1 bg-slate-900 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors shadow-md active:scale-95"
                    >
                      {t("company.services.card.btn_edit")}
                    </button>

                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="px-4 py-3 border border-slate-200 text-slate-300 rounded-xl hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all group/del"
                    >
                      <TrashIcon className="w-5 h-5 transition-transform group-hover/del:scale-110" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <ServiceForm
            service={editingService}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingService(null);
            }}
            isLoading={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};

export default ServicesManagementPage;
